// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Hand-rolled SVG chart builders. Each returns an HTML string; interactive
// behaviour is attached after insertion. Every data mark carries a data-tip
// with exact values — a native <title> is not a tooltip.

import { bars, barBands, gridCells, linear, linePoints, path, runs, ticks, type Rect } from './layout';
import { esc, tipAttr } from '../format';
import type { Val } from '../types';

/**
 * `role="img"` on a chart is only correct when the chart has an accessible NAME
 * and no focusable children. Without a name, a screen reader announces a bare
 * "graphic" and moves on; WITH focusable children inside, role="img" makes the
 * subtree presentational and hides every one of those tab stops from the
 * accessibility tree while leaving them in the tab order — which is worse than
 * no role at all. So: pass a label for a static chart, and pass `interactive`
 * for one whose marks are focusable (it becomes a labelled group instead).
 */
export const svgOpen = (w: number, h: number, cls = 'chart-svg', label?: string, interactive = false) => {
  const role = interactive ? 'group' : 'img';
  const named = label ? ` role="${role}" aria-label="${esc(label)}"` : '';
  return `<svg viewBox="0 0 ${w} ${h}" class="${cls}" preserveAspectRatio="xMidYMid meet"${named}>`;
};

export const svgClose = '</svg>';

/** A serviceable accessible name when the caller does not supply one. */
function describeSeries(series: { label: string }[], labels: string[]): string {
  const names = series.map((s) => s.label).filter(Boolean);
  return `Line chart: ${names.join(' and ') || 'series'}, ${labels[0]} to ${labels[labels.length - 1]}`;
}

export function axisY(box: Rect, domain: [number, number], fmt: (v: number) => string, count = 5): string {
  const y = linear(domain, [box.y + box.h, box.y]);
  return ticks(domain[0], domain[1], count)
    .map((t) => {
      const py = y(t);
      if (!Number.isFinite(py)) return '';
      return `<line class="ax-grid" x1="${box.x}" y1="${py.toFixed(1)}" x2="${(box.x + box.w).toFixed(1)}" y2="${py.toFixed(1)}"/>` +
        `<text class="ax-txt" x="${(box.x - 6).toFixed(1)}" y="${(py + 3).toFixed(1)}" text-anchor="end">${esc(fmt(t))}</text>`;
    })
    .join('');
}

export function axisXCategories(box: Rect, labels: string[], every = 1): string {
  const n = labels.length;
  const step = n > 1 ? box.w / (n - 1) : 0;
  return labels
    .map((label, i) => {
      if (i % every !== 0 && i !== n - 1) return '';
      const x = box.x + step * i;
      return `<text class="ax-txt" x="${x.toFixed(1)}" y="${(box.y + box.h + 15).toFixed(1)}" text-anchor="middle">${esc(label)}</text>`;
    })
    .join('');
}

export function axisXBands(box: Rect, labels: string[], every = 1): string {
  const bands = barBands(labels.length, box.x, box.w);
  return labels
    .map((label, i) => {
      if (i % every !== 0 && i !== labels.length - 1) return '';
      return `<text class="ax-txt" x="${bands[i].cx.toFixed(1)}" y="${(box.y + box.h + 15).toFixed(1)}" text-anchor="middle">${esc(label)}</text>`;
    })
    .join('');
}

export interface LineSeries {
  values: Val[];
  color: string;
  label: string;
  dashed?: boolean;
  width?: number;
}

/**
 * Multi-series line chart. Nulls are drawn as GAPS, never bridged and never
 * zero-filled — a suppressed cost year must not read as a crash to the floor.
 */
export function lineChart(opts: {
  series: LineSeries[];
  labels: string[];
  domain: [number, number];
  width: number;
  height: number;
  fmtY: (v: number) => string;
  tip: (i: number) => string;
  everyX?: number;
  label?: string;
  markers?: { index: number; label: string }[];
}): string {
  const { series, labels, domain, width, height, fmtY, tip } = opts;
  const box: Rect = { x: 52, y: 14, w: width - 68, h: height - 44 };
  let out = svgOpen(width, height, 'chart-svg', opts.label ?? describeSeries(series, labels));
  out += axisY(box, domain, fmtY);
  out += axisXCategories(box, labels, opts.everyX ?? 1);

  for (const m of opts.markers ?? []) {
    const step = labels.length > 1 ? box.w / (labels.length - 1) : 0;
    const x = box.x + step * m.index;
    out += `<line class="marker-line" x1="${x.toFixed(1)}" y1="${box.y}" x2="${x.toFixed(1)}" y2="${box.y + box.h}"/>`;
    out += `<text class="marker-txt" x="${(x + 4).toFixed(1)}" y="${(box.y + 10).toFixed(1)}">${esc(m.label)}</text>`;
  }

  for (const s of series) {
    const pts = linePoints(s.values, box, domain);
    for (const run of runs(pts)) {
      if (run.length === 1) {
        out += `<circle class="mark" cx="${run[0].x.toFixed(1)}" cy="${run[0].y.toFixed(1)}" r="2.5" fill="${s.color}"/>`;
      } else {
        out += `<path d="${path(run)}" fill="none" stroke="${s.color}" stroke-width="${s.width ?? 2}"${s.dashed ? ' stroke-dasharray="4 3"' : ''} stroke-linejoin="round"/>`;
      }
    }
    for (const p of pts) {
      if (p) out += `<circle class="mark" cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2.2" fill="${s.color}"/>`;
    }
  }

  // One full-height hit target per period, so hovering anywhere in the column
  // shows every series' value for that year rather than requiring pixel-perfect
  // aim at a 2px dot.
  const step = labels.length > 1 ? box.w / (labels.length - 1) : 0;
  labels.forEach((_, i) => {
    const x = box.x + step * i;
    out += `<rect class="hit" x="${(x - step / 2).toFixed(1)}" y="${box.y}" width="${Math.max(step, 6).toFixed(1)}" height="${box.h}" fill="transparent" data-tip="${tipAttr(tip(i))}"/>`;
  });

  return out + svgClose;
}

/** Vertical bar chart with one bar per period. */
export function barChart(opts: {
  values: Val[];
  labels: string[];
  domain: [number, number];
  width: number;
  height: number;
  color: (i: number) => string;
  fmtY: (v: number) => string;
  tip: (i: number) => string;
  everyX?: number;
  label?: string;
  onClickAttr?: (i: number) => string;
}): string {
  const box: Rect = { x: 52, y: 14, w: opts.width - 68, h: opts.height - 44 };
  let out = svgOpen(opts.width, opts.height, 'chart-svg', opts.label ?? `Bar chart, ${opts.labels.length} categories`, Boolean(opts.onClickAttr));
  out += axisY(box, opts.domain, opts.fmtY);
  out += axisXBands(box, opts.labels, opts.everyX ?? 1);
  const rects = bars(opts.values, box, opts.domain);
  rects.forEach((r, i) => {
    if (!r) return;
    const extra = opts.onClickAttr ? opts.onClickAttr(i) : '';
    out += `<rect class="mark${extra ? ' mark-click' : ''}" x="${r.x.toFixed(1)}" y="${r.y.toFixed(1)}" width="${r.w.toFixed(1)}" height="${Math.max(r.h, 0.5).toFixed(1)}" fill="${opts.color(i)}" data-tip="${tipAttr(opts.tip(i))}" ${extra}/>`;
  });
  return out + svgClose;
}

/** Stacked area chart. Series must have no nulls (callers zero-fill knowingly). */
export function stackedArea(opts: {
  series: { values: number[]; color: string; label: string }[];
  labels: string[];
  width: number;
  height: number;
  fmtY: (v: number) => string;
  tip: (i: number) => string;
  label?: string;
  asShare?: boolean;
}): string {
  const n = opts.labels.length;
  const totals = Array.from({ length: n }, (_, i) => opts.series.reduce((a, s) => a + (s.values[i] || 0), 0));
  const max = opts.asShare ? 100 : Math.max(1, ...totals);
  const box: Rect = { x: 52, y: 14, w: opts.width - 68, h: opts.height - 44 };
  const y = linear([0, max], [box.y + box.h, box.y]);
  const step = n > 1 ? box.w / (n - 1) : 0;

  let out = svgOpen(opts.width, opts.height, 'chart-svg', opts.label ?? `Stacked area chart of ${opts.series.length} series over ${opts.labels.length} periods`);
  out += axisY(box, [0, max], opts.fmtY);
  out += axisXCategories(box, opts.labels, Math.max(1, Math.round(n / 8)));

  const acc = new Array(n).fill(0);
  for (const s of opts.series) {
    const lower = acc.slice();
    for (let i = 0; i < n; i++) acc[i] += opts.asShare && totals[i] ? ((s.values[i] || 0) / totals[i]) * 100 : s.values[i] || 0;
    const top = acc.map((v, i) => ({ x: box.x + step * i, y: y(v) }));
    const bottom = lower.map((v, i) => ({ x: box.x + step * i, y: y(v) })).reverse();
    out += `<path d="${path(top)} ${path(bottom).replace(/^M/, 'L')} Z" fill="${s.color}" stroke="none" class="mark"/>`;
  }

  for (let i = 0; i < n; i++) {
    const x = box.x + step * i;
    out += `<rect class="hit" x="${(x - step / 2).toFixed(1)}" y="${box.y}" width="${Math.max(step, 6).toFixed(1)}" height="${box.h}" fill="transparent" data-tip="${tipAttr(opts.tip(i))}"/>`;
  }
  return out + svgClose;
}

/** Horizontal ranked bars as HTML (not SVG) so long labels reflow on mobile. */
export function rankedBars(items: {
  label: string;
  value: Val;
  display: string;
  color: string;
  sub?: string;
  tip: string;
  href?: string;
}[]): string {
  const max = Math.max(1, ...items.map((i) => (i.value ?? 0)));
  return `<div class="bars">${items
    .map((it) => {
      const w = it.value === null ? 0 : (it.value / max) * 100;
      const tag = it.href ? 'a' : 'div';
      const attrs = it.href ? ` href="${esc(it.href)}"` : '';
      return `<${tag} class="bar-row${it.href ? ' bar-click' : ''}"${attrs} data-tip="${tipAttr(it.tip)}">
        <span class="bar-lab">${esc(it.label)}</span>
        <span class="bar-track"><span class="bar-fill" style="width:${w.toFixed(2)}%;background:${it.color}"></span></span>
        <span class="bar-val">${esc(it.display)}</span>
        ${it.sub ? `<span class="bar-sub">${esc(it.sub)}</span>` : ''}
      </${tag}>`;
    })
    .join('')}</div>`;
}

/** Matrix heatmap as an HTML table so row/column headers stay readable. */
export function matrix(opts: {
  rows: { label: string; sub?: string; cells: { value: Val; display: string; color: string; tip: string; click?: string }[] }[];
  columns: string[];
  corner?: string;
}): string {
  return `<div class="mx-wrap"><table class="mx">
    <thead><tr><th class="mx-corner">${esc(opts.corner ?? '')}</th>${opts.columns.map((c) => `<th scope="col">${esc(c)}</th>`).join('')}</tr></thead>
    <tbody>${opts.rows
      .map(
        (r) => `<tr><th scope="row">${esc(r.label)}${r.sub ? `<small>${esc(r.sub)}</small>` : ''}</th>${r.cells
          .map(
            (c) => `<td class="mx-cell${c.click ? ' mark-click' : ''}" style="background:${c.color}" data-tip="${tipAttr(c.tip)}"${c.click ?? ''}><span>${esc(c.display)}</span></td>`,
          )
          .join('')}</tr>`,
      )
      .join('')}</tbody></table></div>`;
}

/** Dumbbell rows: two values per row joined by a connector. */
export function dumbbell(opts: {
  rows: { label: string; a: Val; b: Val; tip: string; click?: string }[];
  domain: [number, number];
  colorA: string;
  colorB: string;
  width: number;
  label?: string;
  fmt: (v: number) => string;
}): string {
  const rowH = 26;
  const height = opts.rows.length * rowH + 34;
  const box: Rect = { x: 150, y: 8, w: opts.width - 178, h: opts.rows.length * rowH };
  const x = linear(opts.domain, [box.x, box.x + box.w]);
  let out = svgOpen(opts.width, height, 'chart-svg', opts.label ?? `Comparison of ${opts.rows.length} rows against a reference value`);

  for (const t of ticks(opts.domain[0], opts.domain[1], 5)) {
    const px = x(t);
    out += `<line class="ax-grid" x1="${px.toFixed(1)}" y1="${box.y}" x2="${px.toFixed(1)}" y2="${(box.y + box.h).toFixed(1)}"/>`;
    out += `<text class="ax-txt" x="${px.toFixed(1)}" y="${(box.y + box.h + 16).toFixed(1)}" text-anchor="middle">${esc(opts.fmt(t))}</text>`;
  }

  opts.rows.forEach((r, i) => {
    const cy = box.y + rowH * i + rowH / 2;
    const label = r.label.length > 26 ? `${r.label.slice(0, 25)}…` : r.label;
    out += `<text class="ax-txt row-lab" x="${(box.x - 8).toFixed(1)}" y="${(cy + 3).toFixed(1)}" text-anchor="end">${esc(label)}</text>`;
    if (r.a !== null && r.b !== null) {
      out += `<line class="dumb-link" x1="${x(r.a).toFixed(1)}" y1="${cy.toFixed(1)}" x2="${x(r.b).toFixed(1)}" y2="${cy.toFixed(1)}"/>`;
    }
    if (r.a !== null) out += `<circle class="mark" cx="${x(r.a).toFixed(1)}" cy="${cy.toFixed(1)}" r="5" fill="${opts.colorA}"/>`;
    if (r.b !== null) out += `<circle class="mark" cx="${x(r.b).toFixed(1)}" cy="${cy.toFixed(1)}" r="5" fill="${opts.colorB}"/>`;
    out += `<rect class="hit${r.click ? ' mark-click' : ''}" x="0" y="${(box.y + rowH * i).toFixed(1)}" width="${opts.width}" height="${rowH}" fill="transparent" data-tip="${tipAttr(r.tip)}"${r.click ?? ''}/>`;
  });

  return out + svgClose;
}

/** Small-multiple sparkline used in tables and drawers. */
export function sparkline(values: Val[], color: string, w = 88, h = 24): string {
  const known = values.filter((v): v is number => v !== null && Number.isFinite(v));
  if (known.length < 2) return `<span class="spark-empty">—</span>`;
  const min = Math.min(...known);
  const max = Math.max(...known);
  const box: Rect = { x: 1, y: 2, w: w - 2, h: h - 4 };
  const pts = linePoints(values, box, [min === max ? min - 1 : min, max]);
  let out = `<svg viewBox="0 0 ${w} ${h}" class="spark-svg" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">`;
  for (const run of runs(pts)) {
    if (run.length > 1) out += `<path d="${path(run)}" fill="none" stroke="${color}" stroke-width="1.5"/>`;
    else if (run.length === 1) out += `<circle cx="${run[0].x.toFixed(1)}" cy="${run[0].y.toFixed(1)}" r="1.5" fill="${color}"/>`;
  }
  return out + svgClose;
}

export { gridCells };
